import React from "react";

/**
 * CARTAL official logo — uses the customer-provided brand mark.
 * Available variants:
 *  - `full` (default): full lockup with tagline "DRIVE MORE. LIVE BETTER."
 *  - `mark`: square mark only (gold C + silver car), no wordmark text
 *  - `wordmark`: CARTAL wordmark only (text)
 */

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_carvo-market/artifacts/c5durw9k_B010DD54-BD83-49AB-A5A3-946EA832B9AF.png";

export default function Wordmark({
  size = "md", // sm | md | lg | xl
  variant = "mark", // full | mark | wordmark
  className = "",
}) {
  const sizes = {
    sm: { full: "h-9", mark: "h-8", word: "text-base" },
    md: { full: "h-12", mark: "h-10", word: "text-xl" },
    lg: { full: "h-20", mark: "h-14", word: "text-3xl" },
    xl: { full: "h-32 sm:h-40", mark: "h-24 sm:h-28", word: "text-5xl sm:text-6xl" },
  };
  const s = sizes[size] || sizes.md;

  if (variant === "wordmark") {
    return (
      <span
        dir="ltr"
        className={`inline-flex items-baseline font-wordmark ${s.word} text-[#0a0a0a] leading-none ${className}`}
        style={{ letterSpacing: "0.09em", fontWeight: 900 }}
        aria-label="CARTAL"
        data-testid="cartal-wordmark"
      >
        CARTAL
        <span
          aria-hidden
          className="inline-block rounded-full"
          style={{
            width: "0.28em",
            height: "0.28em",
            background: "#c9a55c",
            marginInlineStart: "0.18em",
            transform: "translateY(0.02em)",
          }}
        />
      </span>
    );
  }

  if (variant === "full") {
    return (
      <img
        src={LOGO_URL}
        alt="CARTAL — Drive more. Live better."
        className={`${s.full} w-auto object-contain ${className}`}
        loading="eager"
        data-testid="cartal-logo-full"
      />
    );
  }

  // variant === "mark" — square crop of just the C + car silhouette
  return (
    <span
      className={`inline-flex items-center gap-2.5 ${className}`}
      aria-label="CARTAL"
      data-testid="cartal-logo-mark"
    >
      <span
        className={`relative inline-block ${s.mark} aspect-square shrink-0 overflow-hidden rounded-md`}
        aria-hidden
      >
        {/* The mark is centered in the upper part of the artwork — crop with object-position.
            overflow-hidden on the wrapper is REQUIRED because the inner img is scaled up. */}
        <img
          src={LOGO_URL}
          alt=""
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
          style={{ objectPosition: "center 22%", transform: "scale(2.2)", transformOrigin: "center 22%" }}
          draggable={false}
        />
      </span>
      <span
        dir="ltr"
        className={`font-wordmark ${s.word} text-[#0a0a0a] leading-none whitespace-nowrap`}
        style={{ letterSpacing: "0.09em", fontWeight: 900 }}
      >
        CARTAL
      </span>
    </span>
  );
}
